import {
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { EMPTY, Observable, of, Subject } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';

import { SessionRating } from 'src/app/shared/models/bff/session-rating.interface';
import { CustomCurrencyPipe } from 'src/app/shared/pipes/currency.pipe';

import { ChargingSession } from '../../../shared/models/bff/charging-session.interface';
import {
  ChargePoint,
  PaymentProvider,
  StationDataInterface,
  Tariff,
} from '../../../shared/models/bff/station-data.interface';
import { SessionService } from '../../../shared/services/session.service';

@Component({
  selector: 'app-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
  standalone: false,
})
export class SummaryComponent implements OnInit, OnDestroy {
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly sessionService: SessionService = inject(SessionService);
  private readonly customCurrencyPipe: CustomCurrencyPipe = inject(CustomCurrencyPipe);

  private readonly destroyed$: Subject<boolean> = new Subject();

  public readonly session$ = input.required<ChargingSession>({ alias: 'session' });
  public readonly station$ = input.required<StationDataInterface>({ alias: 'station' });
  public readonly chargePoint$ = input.required<ChargePoint>({ alias: 'chargePoint' });
  public readonly tariff$ = input.required<Tariff>({ alias: 'tariff' });

  @Output()
  closed: EventEmitter<void> = new EventEmitter();

  public readonly oneLineHeader = window.screen.height < 650;
  public readonly noPadding = window.screen.height < 600;

  public readonly sessionRating$: WritableSignal<SessionRating | undefined> = signal(undefined);
  public readonly grossPrice$: Signal<string> = computed(() => {
    const rating = this.sessionRating$();
    if (rating?.grossCost) {
      return this.customCurrencyPipe.transform(rating.grossCost, this.tariff$().currency);
    } else {
      return this.translate.instant('summary.pending');
    }
  });
  public readonly netPrice$: Signal<string> = computed(() => {
    const netCost = this.sessionRating$()?.netCost;
    const tariff = this.tariff$();
    if (netCost == null) {
      return this.translate.instant('summary.pending');
    } else {
      return this.customCurrencyPipe.transform(netCost, tariff.currency);
    }
  });
  public readonly vatAmount$: Signal<string> = computed(() => {
    const vatCost = this.sessionRating$()?.vatCost;
    const tariff = this.tariff$();
    if (vatCost == null) {
      return this.translate.instant('summary.pending');
    } else {
      return this.customCurrencyPipe.transform(vatCost, tariff.currency);
    }
  });
  public readonly vatPercent$: Signal<string> = computed(() => {
    const vatPercent = this.sessionRating$()?.vatPercent;
    if (vatPercent == null) {
      return '';
    } else {
      return ` (${vatPercent} %)`;
    }
  });
  public readonly isStripe$: Signal<boolean> = computed(
    () => PaymentProvider.PAYONE_LEGACY !== this.station$().paymentProvider
  );
  public readonly sessionDate$: Signal<string> = computed(() => {
    const session = this.session$();
    return this.parseDateToLocale(session?.date);
  });

  public readonly receiptAvailable$: Signal<boolean> = computed(() => {
    const rating = this.sessionRating$();
    return !!rating?.cfeReceiptDownloadId;
  });
  public readonly showVat$: Signal<boolean> = computed(() => {
    const rating = this.sessionRating$();
    return rating?.netCost !== 0;
  });

  public readonly errorMsg$: WritableSignal<string | null> = signal(null);
  public readonly summaryReady$: WritableSignal<boolean> = signal(false);

  ngOnInit() {
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
  }

  private startPolling() {
    const sessionId: string | null = localStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('SummaryComponent#startPolling: no session id is available');
    }
    this.sessionService
      .isBdrAvailable(sessionId)
      .pipe(
        switchMap(isBdrAvailable => {
          if (isBdrAvailable) {
            return this.sessionService.pollSessionRating(sessionId);
          }
          throw new Error('SummaryComponent#startPolling: bdr is not available');
        }),
        catchError(error => {
          console.error('SummaryComponent#startPolling: could not fetch session rating', error);
          this.sessionRating$.set({
            vatCost: 0,
            grossCost: 0,
            netCost: 0,
            vatPercent: 0,
            billable: false,
          });
          this.summaryReady$.set(true);
          this.errorMsg$.set('summary.error-no-rating');
          return EMPTY;
        }),
        switchMap((rating: SessionRating): Observable<SessionRating> => {
          this.sessionRating$.set(rating);
          if (!this.isStripe$()) {
            this.summaryReady$.set(true);
            return EMPTY;
          }
          if (!rating.cfeReceiptDownloadId) {
            return this.sessionService.pollSessionForReceipt(sessionId);
          }
          return of(rating);
        }),
        catchError(error => {
          console.error('SummaryComponent#startPolling, receipt could not be downloaded ', error);
          this.summaryReady$.set(true);
          this.errorMsg$.set('summary.error-no-receipt');
          return EMPTY;
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe(rating => {
        this.summaryReady$.set(true);
        this.sessionRating$.set(rating);
      });
  }

  public savePDF(): void {
    const cfeReceiptDownloadId = this.sessionRating$()?.cfeReceiptDownloadId;
    if (cfeReceiptDownloadId) {
      const fileName = `${this.translate.instant('summary.filename-receipt')}-${this.parseDateToLocale(this.session$()?.date)}.pdf`;
      this.sessionService.fetchInvoicePdfBlob(cfeReceiptDownloadId).subscribe(blob => {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        anchor.target = '_blank';
        anchor.click();
        setTimeout(() => URL.revokeObjectURL(url), 100);
      });
    }
  }

  public close(): void {
    this.closed.emit();
  }

  private parseDateToLocale(date: string | undefined): string {
    if (date) {
      return moment(date).format('lll');
    } else {
      return '';
    }
  }
}
